import { Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'
import { MdUpload } from 'react-icons/md'

import { NotificationType, notify } from 'src/providers/NotificationProvider'
import { useUser } from 'src/providers/UserProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Divider } from 'src/components/atoms/Divider'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { ParagraphSmaller } from 'src/components/atoms/Typography'
import { FileType } from 'src/components/molecules/FileUpload'
import { FormField } from 'src/components/molecules/FormField'
import { Thumbnails } from 'src/components/molecules/Thumbnails'

import {
  useCreateRegistryMutation,
  useGeneratePutObjectPresignedUrlMutation,
  useGetAdminRegistryViewLazyQuery,
  useGetRegistryViewLazyQuery,
  useUpdateRegistryMutation
} from 'src/config/generated/graphql'
import { adminListRegistriesPath, listMyRegistriesPath } from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import { NotFound } from 'src/pages/404/404'
import { FormFieldTypes, S3Asset } from 'src/types/form'
import { trpc } from 'src/utils/trpc'

import { buildInitialValues, transformFormData } from './helper'
import { RegistryFormValues } from './types'
import { validationSchema } from './validationSchema'

export const RegistryForm = ({
  isAdmin = false,
  registryId
}: {
  isAdmin?: boolean
  registryId?: string
}) => {
  const router = useRouter()
  const { user } = useUser()
  const { invalidateQueries } = trpc.useContext()

  const { accountId } = router.query as { accountId: string }

  const [
    getAdminRegistryView,
    {
      called: adminRegistryCalled,
      data: adminRegistryData,
      loading: adminRegistryLoading
    }
  ] = useGetAdminRegistryViewLazyQuery()

  const [
    getRegistryView,
    { called: registryCalled, data: registryData, loading: registryLoading }
  ] = useGetRegistryViewLazyQuery()

  const registry = useMemo(() => {
    if (isAdmin) {
      return adminRegistryData?.RegistryView?.[0]
    }
    return registryData?.RegistryView?.[0]
  }, [isAdmin, adminRegistryData, registryData])

  useEffect(() => {
    if (registryId) {
      if (isAdmin) {
        getAdminRegistryView({ variables: { id: registryId } })
      } else if (user?.Account?.FunderId) {
        getRegistryView({
          variables: { id: registryId, funderId: user?.Account?.FunderId }
        })
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryId, user?.Account?.FunderId])

  const [
    generatePutObjectPresignedUrl,
    { loading: generatePutObjectPresignedUrlLoading }
  ] = useGeneratePutObjectPresignedUrlMutation()

  const handleOnCompleted = async () => {
    await invalidateQueries(['registries.getByUri', { uri: registry?.Uri! }])
    await invalidateQueries('registries.all')
    const action = registry ? 'updated' : 'created'
    notify({
      message: `Successfully ${action} registry.`,
      type: NotificationType.success
    })
    if (isAdmin) {
      router.push(adminListRegistriesPath)
    } else {
      router.push(listMyRegistriesPath)
    }
  }

  const [createRegistry, { loading: createRegistryLoading }] =
    useCreateRegistryMutation({ onCompleted: handleOnCompleted })

  const [updateRegistry, { loading: updateRegistryLoading }] =
    useUpdateRegistryMutation({ onCompleted: handleOnCompleted })

  const initialValues: RegistryFormValues = useMemo(
    () => buildInitialValues(registry, accountId),
    [registry, accountId]
  )

  const handleSubmit = async (values: RegistryFormValues) => {
    let input = transformFormData(values, isAdmin, !!registry)
    const heroFile = values.HeroFile?.[0] as File

    if (heroFile) {
      const { data } = await generatePutObjectPresignedUrl({
        variables: {
          input: {
            FileName: heroFile.name,
            ContentType: heroFile.type,
            IsPublic: true
          }
        }
      })

      const { S3Key, SignedUrl } = data?.generate_put_object_presigned_url || {}
      if (!S3Key || !SignedUrl) return

      // Upload image to S3
      await fetch(SignedUrl, { method: 'PUT', mode: 'cors', body: heroFile })
      input = { ...input, Hero: S3Key }
    }

    return registry
      ? updateRegistry({ variables: { input, id: registry.Id! } })
      : createRegistry({ variables: { input } })
  }

  // If we have a registry Id (e.g. we are on an 'edit' rather than a 'create'
  // path) and we do not having a matching registry (or the user doesn't have
  // permission to view it) then render the Not Found page
  if (
    (registryId &&
      !isAdmin &&
      registryCalled &&
      !registryLoading &&
      !registry) ||
    (registryId &&
      isAdmin &&
      adminRegistryCalled &&
      !adminRegistryLoading &&
      !registry) ||
    (registryId && !isAdmin && !user?.Account?.FunderId)
  )
    return <NotFound />

  return (
    <LoadingSpinner isLoading={adminRegistryLoading || registryLoading}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ dirty, isSubmitting, values }) => {
          const heroUri =
            values.HeroFile?.[0]?.Uri ||
            `${env.NEXT_PUBLIC_ASSETS_DOMAIN}/${values.Hero}`

          const heroThumbnail: S3Asset[] = [{ Uri: heroUri } as S3Asset]

          return (
            <Form>
              {isAdmin ? (
                <FormField
                  id='AccountId'
                  label='Account to create registry for'
                  type={FormFieldTypes.accountSelect}
                  disabled
                />
              ) : null}
              <FormField
                id='Name'
                label='Name your registry'
                description='This is the title for your Tree Registry, it should be punchy, concise, and unique to you.'
                type={FormFieldTypes.text}
              />
              {isAdmin ? (
                <FormField
                  id='IsCampaign'
                  label=''
                  checkboxLabel='Create Registry as crowdfund campaign'
                  type={FormFieldTypes.checkbox}
                />
              ) : null}
              {isAdmin && values.IsCampaign ? (
                <FormField
                  id='Uri'
                  label='Uri'
                  description='The path in the url through which people can view your registry page.'
                  type={FormFieldTypes.text}
                  disabled={!!registry}
                />
              ) : null}
              {isAdmin && values.IsCampaign ? (
                <FormField
                  id='Summary'
                  label='Summary'
                  description='A short summary to be displayed along with your registry name.'
                  type={FormFieldTypes.textarea}
                />
              ) : null}
              <FormField
                id='Description'
                label='Description'
                description='Inform people visiting your Tree Registry about why you started a page, what motivates you, or why they should gift.'
                type={FormFieldTypes.textarea}
              />
              <Divider />
              <FormField
                id='HeroFile'
                className='mb-0'
                fileType={FileType.image}
                description={`It's best to upload an image that is square.`}
                label='Choose an image for your Tree Registry'
                maxFiles={1}
                renderDropzone={false}
                renderFilesList={false}
                replaceFiles
                type={FormFieldTypes.file}
                uploadButton={
                  <ButtonRebrand variant='forest'>
                    <MdUpload className='mr-2' />
                    <span>Upload image</span>
                  </ButtonRebrand>
                }
              />
              <Thumbnails files={heroThumbnail} />
              <FormField
                id='IsHidden'
                className='-mb-2'
                label=''
                checkboxLabel='Make Tree Registry private'
                type={FormFieldTypes.checkbox}
              />
              <ParagraphSmaller className='form-description-text mb-6'>
                If private, your page will be hidden from search, boards, and
                external search engines.
              </ParagraphSmaller>
              <FormField
                id='ClosesOn'
                label='Closing date'
                description='You can create a timed Tree Registry, this can be used in building a sense of urgency to reach your specific tree goals. When your closing date is reached, your Tree Registry will no longer be able to accept gifts.'
                type={FormFieldTypes.datetime}
              />
              {isAdmin || registry?.Gifts === 0 ? (
                <>
                  <FormField
                    id='IsClosed'
                    className='-mb-2'
                    label=''
                    checkboxLabel='Mark Tree Registry as closed'
                    type={FormFieldTypes.checkbox}
                  />
                  <ParagraphSmaller className='form-description-text mb-6'>
                    If closed, users will not be able to add to a registry, or
                    edit their registry. Registries are automatically closed
                    once their closing date is reached.
                  </ParagraphSmaller>
                </>
              ) : null}

              <Divider />
              <FormField
                id='RegionId'
                label='Planting region'
                description={`We'll match your trees to a project in this region as best we can. Matching your trees to a region depends on the planting projects we have that need support. We can't always guarantee a match but we'll do our best.`}
                type={FormFieldTypes.regionSelect}
                regionsOnly
              />
              <ButtonRebrand
                variant='lime'
                disabled={
                  !dirty ||
                  isSubmitting ||
                  createRegistryLoading ||
                  generatePutObjectPresignedUrlLoading ||
                  updateRegistryLoading
                }
                type='submit'
              >
                Save
              </ButtonRebrand>
            </Form>
          )
        }}
      </Formik>
    </LoadingSpinner>
  )
}
